/* Book Lab · Home dashboard
   Route: /book-lab/editor/home
   Renders real books via GET /api/book-lab/books and stats via
   GET /api/book-lab/home/stats. Reuses ExportBookModal and the canonical
   POST /api/book-lab/books flow. Layout inspired by the supplied
   HTML mock but ported to the existing PremierLab CRA stack. */
import { useCallback, useEffect, useMemo, useState, Component } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Plus, Search, BookOpen, LayoutGrid, List, ChevronRight, MoreVertical,
  Layers, Sparkles, Download, Upload, Trash2, Copy, Home, Folder,
  Loader2, ArrowRight, FileText, PenSquare,
} from "lucide-react";
import { api } from "../../../lib/apiClient";
import { useAuth } from "../../../contexts/AuthContext";
import { ExportBookModal } from "../ExportBookModal";
import BookSetupFlow from "../setup/BookSetupFlow";
import {
  bookLabWriterUrl, bookLabCanvasUrl, pickLatestBook, resolveBookId,
} from "../../../lib/bookLabRoutes";

function logBookLabClick(payload) {
  // Temporary production diagnostics for click / navigation failures.
  console.info("[BookLabClick]", payload);
}

/** Catch BookSetupFlow render crashes so openNew is not a silent no-op. */
class SetupErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }
  static getDerivedStateFromError(error) {
    return { error };
  }
  componentDidCatch(error, info) {
    console.error("[BookLabSetup]", error, info);
  }
  render() {
    if (this.state.error) {
      return (
        <div
          data-testid="bl-setup-error"
          role="alert"
          style={{
            position: "fixed", inset: 0, zIndex: 92000, display: "grid", placeItems: "center",
            background: "rgba(0,0,0,0.7)", color: "#FCA5A5", padding: 24, fontFamily: "Inter, system-ui, sans-serif",
          }}
        >
          <div style={{ maxWidth: 420, textAlign: "center" }}>
            <div style={{ fontWeight: 800, marginBottom: 8 }}>Book setup failed to open</div>
            <div style={{ fontSize: 13, color: "#E6EFEA", marginBottom: 16 }}>
              {this.state.error?.message || "Unknown error"}
            </div>
            <button type="button" onClick={this.props.onClose}
              style={{ padding: "10px 14px", borderRadius: 10, border: 0, cursor: "pointer",
                background: "linear-gradient(135deg,#1FB870,#41E19A)", color: "#04150E", fontWeight: 700 }}>
              Close
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

const STATUSES = [
  { key: "all",         label: "All projects" },
  { key: "in_progress", label: "In progress" },
  { key: "draft",       label: "Draft" },
  { key: "completed",   label: "Completed" },
];

// Deterministic cover palette by book ID so covers stay stable across reloads.
const COVER_PALETTES = [
  { bg: "linear-gradient(135deg,#0F2E24 0%,#0B4634 50%,#0F5C42 100%)", accent: "#7DE4B1", tag: "#7DE4B1" },
  { bg: "linear-gradient(140deg,#0C1F2C 0%,#12314A 60%,#194B69 100%)", accent: "#78C5FF", tag: "#78C5FF" },
  { bg: "linear-gradient(135deg,#2E1A0C 0%,#5A3418 55%,#7A4820 100%)", accent: "#FFD08A", tag: "#FFD08A" },
  { bg: "linear-gradient(135deg,#1A1030 0%,#2C1856 50%,#3B2274 100%)", accent: "#C6A5FF", tag: "#C6A5FF" },
  { bg: "linear-gradient(135deg,#301322 0%,#4E1D3B 50%,#6C2A55 100%)", accent: "#FFB0D1", tag: "#FFB0D1" },
  { bg: "linear-gradient(135deg,#0E2C2E 0%,#12484A 55%,#186B6D 100%)", accent: "#8FE8E8", tag: "#8FE8E8" },
];
const pick = (id) => {
  const h = String(id || "").split("").reduce((a, c) => a + c.charCodeAt(0), 0);
  return COVER_PALETTES[h % COVER_PALETTES.length];
};

const humanTime = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso); const now = new Date();
  const s = Math.max(1, (now - d) / 1000);
  if (s < 60)     return "Just now";
  if (s < 3600)   return `${Math.floor(s / 60)} min ago`;
  if (s < 86400)  return `${Math.floor(s / 3600)} h ago`;
  if (s < 172800) return "Yesterday";
  if (s < 2592000)return `${Math.floor(s / 86400)} d ago`;
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
};

const statusOf = (b) => {
  if (!b) return "draft";
  const s = (b.status || "").toLowerCase();
  if (s === "completed") return "completed";
  if (s === "outline_ready" || s === "generating" || s === "chapters_ready" ||
      (b.completion_percent && b.completion_percent > 0 && b.completion_percent < 100))
    return "in_progress";
  return "draft";
};

const compact = (n) => {
  if (n == null) return "—";
  if (n < 1000) return String(n);
  if (n < 1_000_000) return `${(n / 1000).toFixed(n < 10_000 ? 1 : 0).replace(".0", "")}K`;
  return `${(n / 1_000_000).toFixed(1)}M`;
};

export default function BookLabHome() {
  const nav = useNavigate();
  const { user } = useAuth();
  const [books, setBooks] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");
  const [view, setView] = useState(() => localStorage.getItem("pl:booklab:view") || "grid");
  const [openNew, setOpenNew] = useState(false);
  const [exportFor, setExportFor] = useState(null);
  const [menuFor, setMenuFor] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setErr(null);
    try {
      const [b, s] = await Promise.all([
        api.get("/api/book-lab/books?limit=50"),
        api.get("/api/book-lab/home/stats"),
      ]);
      setBooks(b.items || []); setStats(s || null);
    } catch (e) { setErr(e.message || "Could not load Book Lab"); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { localStorage.setItem("pl:booklab:view", view); }, [view]);

  const filtered = useMemo(() => {
    let arr = books;
    if (status !== "all") arr = arr.filter((b) => statusOf(b) === status);
    const s = q.trim().toLowerCase();
    if (s) arr = arr.filter((b) =>
      [b.title, b.subtitle, b.author].some((v) => (v || "").toLowerCase().includes(s))
    );
    return arr;
  }, [books, q, status]);

  const latest = useMemo(() => pickLatestBook(books), [books]);
  const firstName = (user?.name || user?.email || "").split(" ")[0] || (user?.email || "").split("@")[0] || "there";

  const openSetup = (source) => {
    logBookLabClick({ action: "open-setup", source, book: null, resolvedBookId: null, destination: null });
    setOpenNew(true);
  };

  const openProject = (bookOrId, source = "open-project") => {
    const resolvedBookId = resolveBookId(bookOrId);
    const destination = bookLabWriterUrl(resolvedBookId);
    logBookLabClick({
      action: source,
      book: typeof bookOrId === "object" ? bookOrId : null,
      resolvedBookId,
      destination,
    });
    if (!resolvedBookId) {
      setErr("This book is missing an id and cannot be opened.");
      return;
    }
    nav(destination);
  };

  const openCanvas = (bookOrId, event, source = "open-canvas") => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    const resolvedBookId = resolveBookId(bookOrId);
    const destination = bookLabCanvasUrl(resolvedBookId);
    logBookLabClick({
      action: source,
      book: typeof bookOrId === "object" ? bookOrId : null,
      resolvedBookId,
      destination,
    });
    if (!resolvedBookId) {
      setErr("This book is missing an id and cannot open in Canvas.");
      return;
    }
    nav(destination);
  };

  const duplicate = async (b) => {
    const bid = resolveBookId(b);
    setBusyId(bid); setMenuFor(null);
    try {
      const dup = await api.post(`/api/book-lab/books/${bid}/duplicate`);
      await load();
      openProject(dup, "duplicate");
    } catch (e) { setErr(e.message); } finally { setBusyId(null); }
  };

  const trash = async (b) => {
    const bid = resolveBookId(b);
    if (!window.confirm(`Move "${b.title}" to trash? This deletes the book permanently in this iteration (soft-trash lands next turn).`)) return;
    setBusyId(bid); setMenuFor(null);
    try {
      await api.del(`/api/book-lab/books/${bid}`);
      await load();
    } catch (e) { setErr(e.message); } finally { setBusyId(null); }
  };

  return (
    <div className="pl-bl-home" data-testid="book-lab-home" onClick={() => setMenuFor(null)}>
      <style>{styles}</style>
      <aside className="bl-sidebar">
        <div className="bl-brand">
          <div className="bl-brand-mark"><BookOpen size={18} /></div>
          <div>
            <div className="bl-brand-title">Book Lab</div>
            <div className="bl-brand-sub">by PremierLab</div>
          </div>
        </div>
        <button type="button" className="bl-new-btn" onClick={() => openSetup("sidebar-new")} data-testid="bl-side-new">
          <Plus size={14} /> New book
        </button>
        <nav className="bl-nav">
          {[
            { icon: Home, label: "Home", to: "/book-lab/editor/home", active: true, testId: "bl-nav-home" },
            { icon: Folder, label: "All books", to: "/book-lab/editor/home", testId: "bl-nav-books" },
            { icon: Layers, label: "Templates", disabled: true },
            { icon: Upload, label: "Imports", disabled: true },
            { icon: PenSquare, label: "Assets", disabled: true },
            { icon: Download, label: "Exports", disabled: true },
            { icon: Trash2, label: "Trash", disabled: true },
          ].map((it) => (
            it.disabled ? (
              <div key={it.label} className="bl-nav-item disabled" title="Ships in next iteration">
                <it.icon size={13} /> {it.label}
                <span className="bl-nav-soon">Soon</span>
              </div>
            ) : (
              <Link key={it.label} to={it.to} data-testid={it.testId}
                    className={"bl-nav-item " + (it.active ? "active" : "")}>
                <it.icon size={13} /> {it.label}
              </Link>
            )
          ))}
        </nav>
      </aside>

      <main className="bl-main">
        <div className="bl-topbar">
          <div className="bl-crumbs">
            <Link to="/dashboard" className="bl-crumb-link">PremierLab</Link>
            <ChevronRight size={12} className="bl-crumb-sep" />
            <span className="bl-crumb-current">Book Lab</span>
          </div>
          <div className="bl-search">
            <Search size={13} />
            <input placeholder="Search your books" value={q}
                   onChange={(e) => setQ(e.target.value)} data-testid="bl-search" />
          </div>
        </div>

        <section className="bl-hero">
          <div className="bl-hero-left">
            <div className="bl-eyebrow"><span className="bl-eyebrow-dot" /> AI publishing workspace</div>
            <h1>Welcome back, <span>{firstName}.</span></h1>
            <p className="bl-hero-copy">
              Continue your latest manuscript, generate a new book with AI, or open
              the visual canvas to design a publishing-ready layout.
            </p>
            <div className="bl-hero-actions">
              <button type="button" className="bl-btn-primary" onClick={() => openSetup("hero-create")} data-testid="bl-hero-new">
                <Plus size={14} /> Create new book
              </button>
              {latest ? (
                <button type="button" className="bl-btn-ghost" onClick={() => openProject(latest, "hero-continue")} data-testid="bl-hero-continue">
                  <BookOpen size={14} /> Continue “{(latest.title || "Untitled").slice(0, 32)}”
                </button>
              ) : (
                <button type="button" className="bl-btn-ghost" onClick={() => openSetup("hero-first")} data-testid="bl-hero-first">
                  <Plus size={14} /> Create your first book
                </button>
              )}
            </div>
          </div>
          <div className="bl-hero-preview" aria-hidden>
            {(latest ? [latest, ...books.filter((b) => resolveBookId(b) !== resolveBookId(latest)).slice(0, 2)] : []).slice(0, 3).map((b, i) => (
              <MiniCover key={resolveBookId(b) || `mini-${i}`} book={b} idx={i} />
            ))}
            {!latest && <MiniCover book={{ id: "seed", title: "Your First Book", author: "PremierLab" }} idx={0} />}
          </div>
        </section>

        <section className="bl-stats">
          <StatCard label="Total books" value={compact(stats?.total_books)} icon={BookOpen} />
          <StatCard label="Words generated" value={compact(stats?.words_generated)} icon={FileText} />
          <StatCard label="Completed exports" value={compact(stats?.completed_exports)} icon={Download} />
          <StatCard label="AI credits" value={stats?.credits_remaining == null ? "—" : compact(stats.credits_remaining)} icon={Sparkles} />
        </section>

        <section className="bl-section">
          <div className="bl-section-head">
            <h2>Recent work</h2>
            <div className="bl-controls">
              <select data-testid="bl-status-filter" value={status}
                      onChange={(e) => setStatus(e.target.value)} className="bl-select">
                {STATUSES.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
              </select>
              <div className="bl-viewtoggle">
                <button data-testid="bl-view-grid" onClick={() => setView("grid")}
                        className={view === "grid" ? "active" : ""} aria-label="Grid view">
                  <LayoutGrid size={13} />
                </button>
                <button data-testid="bl-view-list" onClick={() => setView("list")}
                        className={view === "list" ? "active" : ""} aria-label="List view">
                  <List size={13} />
                </button>
              </div>
            </div>
          </div>

          {loading ? (
            <div className="bl-grid">
              {[0, 1, 2, 3].map((i) => <div key={i} className="bl-card bl-skel" />)}
            </div>
          ) : err ? (
            <div className="bl-empty">
              <div className="bl-empty-title">Could not load your books</div>
              <div className="bl-empty-sub">{err}</div>
              <button className="bl-btn-ghost" onClick={load}>Retry</button>
            </div>
          ) : filtered.length === 0 ? (
            <div className="bl-empty" data-testid="bl-empty">
              <div className="bl-empty-title">
                {q || status !== "all" ? "No books match those filters" : "You don`t have any books yet"}
              </div>
              <div className="bl-empty-sub">
                {q || status !== "all"
                  ? "Try clearing filters or searching for a different word."
                  : "Start with AI, an import, or a blank canvas — you can always change your mind later."}
              </div>
              {(q || status !== "all") ? (
                <button className="bl-btn-ghost" onClick={() => { setQ(""); setStatus("all"); }}>
                  Clear filters
                </button>
              ) : (
                <button type="button" className="bl-btn-primary" onClick={() => openSetup("empty-create")}>
                  <Plus size={14} /> Create your first book
                </button>
              )}
            </div>
          ) : (
            <div className={view === "grid" ? "bl-grid" : "bl-list"}>
              {filtered.map((b) => {
                const bid = resolveBookId(b);
                return (
                <ProjectCard
                  key={bid || b.title}
                  book={b}
                  bookId={bid}
                  view={view}
                  busy={busyId === bid}
                  menuOpen={menuFor === bid}
                  onMenu={(e) => { e.stopPropagation(); setMenuFor((v) => v === bid ? null : bid); }}
                  onOpen={() => openProject(b, "card-open")}
                  onCanvas={(e) => openCanvas(b, e, "card-canvas")}
                  onDuplicate={() => duplicate(b)}
                  onExport={() => { setExportFor(b); setMenuFor(null); }}
                  onTrash={() => trash(b)}
                />
                );
              })}
            </div>
          )}
        </section>

        <section className="bl-section">
          <div className="bl-section-head"><h2>Quick start</h2></div>
          <div className="bl-qs">
            <QuickCard icon={Sparkles} title="Start with AI" desc="Generate a title, outline, chapters, and full manuscript."
                       onClick={() => openSetup("qs-ai")} testId="qs-ai" />
            <QuickCard icon={Upload} title="Import manuscript" desc="Upload TXT, Markdown, or DOCX and keep editing."
                       onClick={() => openSetup("qs-import")} testId="qs-import" tone="blue" />
            <QuickCard icon={Layers} title="Use a template" desc="Ships next iteration — start from a professional layout."
                       disabled tone="orange" testId="qs-template" />
            <QuickCard icon={PenSquare} title="Blank canvas"
                       desc="Open a clean layout workspace and design every page manually."
                       onClick={() => {
                         const destination = "/book-lab/editor?view=canvas";
                         logBookLabClick({ action: "qs-canvas", book: null, resolvedBookId: null, destination });
                         nav(destination);
                       }} tone="purple" testId="qs-canvas" />
          </div>
        </section>
      </main>

      {openNew && (
        <SetupErrorBoundary onClose={() => setOpenNew(false)}>
          <BookSetupFlow
            onClose={() => {
              logBookLabClick({ action: "setup-close", book: null, resolvedBookId: null, destination: null });
              setOpenNew(false);
            }}
            onCreated={(b) => {
              const resolvedBookId = resolveBookId(b);
              const destination = bookLabWriterUrl(resolvedBookId);
              logBookLabClick({ action: "setup-created", book: b, resolvedBookId, destination });
              setOpenNew(false);
              load();
              if (resolvedBookId) nav(destination);
            }}
          />
        </SetupErrorBoundary>
      )}
      {exportFor && (
        <ExportBookModal bookId={resolveBookId(exportFor)} bookTitle={exportFor.title}
                         onClose={() => setExportFor(null)} />
      )}
    </div>
  );
}

/* -------- Sub-components -------- */
function MiniCover({ book, idx }) {
  const pal = pick(resolveBookId(book));
  const rotate = [-4, 6, -8][idx] ?? 0;
  const translate = [0, 24, -20][idx] ?? 0;
  return (
    <div className="bl-mini-cover" style={{
      background: pal.bg, transform: `rotate(${rotate}deg) translateX(${translate}px)`,
    }}>
      <div className="bl-mini-tag" style={{ color: pal.tag }}>{book.book_type || "PremierLab"}</div>
      <div className="bl-mini-title">{book.title}</div>
      <div className="bl-mini-line" style={{ background: pal.accent }} />
      <div className="bl-mini-author">{book.author || "PremierLab"}</div>
    </div>
  );
}

function StatCard({ label, value, icon: Icon }) {
  return (
    <div className="bl-stat" data-testid={`bl-stat-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="bl-stat-label">{label}</div>
      <div className="bl-stat-row">
        <div className="bl-stat-value">{value}</div>
        <div className="bl-stat-icon"><Icon size={14} /></div>
      </div>
    </div>
  );
}

function QuickCard({ icon: Icon, title, desc, onClick, tone, disabled, testId }) {
  return (
    <button className={"bl-quick " + (tone ? `tone-${tone}` : "") + (disabled ? " disabled" : "")}
            onClick={disabled ? undefined : onClick} disabled={disabled} data-testid={testId}>
      <div className={"bl-quick-icon tone-" + (tone || "green")}><Icon size={16} /></div>
      <div className="bl-quick-title">{title}</div>
      <div className="bl-quick-desc">{desc}</div>
    </button>
  );
}

function ProjectCard({ book: b, bookId, view, busy, menuOpen, onMenu, onOpen, onCanvas, onDuplicate, onExport, onTrash }) {
  const bid = bookId || resolveBookId(b);
  const pal = pick(bid);
  const st = statusOf(b);
  const stLabel = st === "in_progress" ? "In progress" : st === "completed" ? "Completed" : "Draft";
  const pct = b.completion_percent ?? 0;
  return (
    <article className={"bl-card " + (view === "list" ? "list" : "")}
             data-testid={`bl-card-${bid}`} onClick={onOpen}>
      <div className="bl-card-cover" style={{ background: pal.bg }}>
        <span className={"bl-card-status status-" + st}>{stLabel}</span>
        <button type="button" className="bl-card-menu" onClick={onMenu} aria-label="Project menu"
                data-testid={`bl-menu-${bid}`}>
          {busy ? <Loader2 size={14} className="animate-spin" /> : <MoreVertical size={14} />}
        </button>
        <div className="bl-card-inner">
          <div className="bl-card-tag" style={{ color: pal.tag }}>{b.book_type || "Book"}</div>
          <div className="bl-card-title">{b.title}</div>
          <div className="bl-card-decor" style={{ background: pal.accent }} />
          <div className="bl-card-author">{b.author || "PremierLab"}</div>
        </div>
        {menuOpen && (
          <div className="bl-card-menu-drop" onClick={(e) => e.stopPropagation()}
               data-testid={`bl-menu-drop-${bid}`}>
            <button type="button" onClick={onOpen}><BookOpen size={13} /> Open project</button>
            <button type="button" onClick={(e) => onCanvas(e)}><Layers size={13} /> Open in Canvas</button>
            <button type="button" onClick={onDuplicate}><Copy size={13} /> Duplicate</button>
            <button type="button" onClick={onExport}><Download size={13} /> Export</button>
            <div className="bl-menu-sep" />
            <button type="button" className="danger" onClick={onTrash}><Trash2 size={13} /> Move to trash</button>
          </div>
        )}
      </div>
      <div className="bl-card-info">
        <div className="bl-card-info-row">
          <div className="bl-card-info-title">{b.title}</div>
          <div className="bl-card-info-fmt">{b.book_type || "Book"}</div>
        </div>
        <div className="bl-card-meta">
          <span>Edited {humanTime(b.updated_at || b.created_at)}</span>
          <span className="bl-dot" />
          <span>{b.page_count || 0} pages</span>
          <span className="bl-dot" />
          <span>{b.chapter_count || 0} ch</span>
        </div>
        <div className="bl-progress">
          <div className="bl-progress-head">
            <span>Completion</span><span>{pct}%</span>
          </div>
          <div className="bl-progress-track"><div className="bl-progress-fill" style={{ width: `${pct}%` }} /></div>
        </div>
        <div className="bl-card-actions">
          <button type="button" className="bl-btn-small" onClick={(e) => { e.stopPropagation(); onOpen(); }}
                  data-testid={`bl-open-${bid}`}>
            Open project <ArrowRight size={11} />
          </button>
          <button
            type="button"
            className="bl-icon-btn"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onCanvas(e);
            }}
            aria-label="Open this book in Canvas"
            title="Open in Canvas"
            data-testid={`bl-canvas-${bid}`}
          >
            <Layers size={13} />
          </button>
        </div>
      </div>
    </article>
  );
}

/* -------- CSS -------- */
const styles = `
.pl-bl-home { min-height: 100vh; background: #071414; color: #E6EFEA;
  font-family: Inter, system-ui, sans-serif; display: grid;
  grid-template-columns: 240px 1fr; }
.bl-sidebar { position: sticky; top: 0; height: 100vh; padding: 20px 14px;
  background: rgba(9,20,20,0.75); backdrop-filter: blur(20px);
  border-right: 1px solid rgba(255,255,255,0.06); display: flex; flex-direction: column; gap: 14px; }
.bl-brand { display: flex; align-items: center; gap: 10px; padding: 6px 4px; }
.bl-brand-mark { height: 34px; width: 34px; border-radius: 10px; display: grid; place-items: center;
  background: linear-gradient(135deg,#0F3B2B 0%,#155F44 100%); color: #7DE4B1; }
.bl-brand-title { font-weight: 800; font-size: 13.5px; letter-spacing: -.2px; }
.bl-brand-sub { font-size: 10.5px; color: #7B8A85; }
.bl-new-btn { display: inline-flex; align-items: center; justify-content: center; gap: 8px;
  padding: 10px 12px; border: 0; border-radius: 10px; cursor: pointer; font-weight: 700; font-size: 12.5px;
  background: linear-gradient(135deg,#1FB870 0%,#41E19A 100%); color: #04150E; }
.bl-new-btn:hover { filter: brightness(1.05); }
.bl-nav { display: flex; flex-direction: column; gap: 2px; margin-top: 4px; }
.bl-nav-item { display: flex; align-items: center; gap: 10px; padding: 8px 10px; border-radius: 8px;
  color: #9BABA6; font-size: 12.5px; text-decoration: none; cursor: pointer; border: 0; background: transparent; }
.bl-nav-item:hover { color: #E6EFEA; background: rgba(255,255,255,0.03); }
.bl-nav-item.active { background: rgba(65,225,154,0.10); color: #7DE4B1; font-weight: 700; }
.bl-nav-item.disabled { opacity: .55; cursor: not-allowed; }
.bl-nav-soon { margin-left: auto; font-size: 9px; padding: 2px 6px; border-radius: 8px;
  background: rgba(255,255,255,0.05); color: #6E7C77; text-transform: uppercase; letter-spacing: .1em; }
.bl-main { min-width: 0; padding: 20px 28px 60px; }
.bl-topbar { display: flex; align-items: center; gap: 16px; padding: 4px 2px 22px; }
.bl-crumbs { display: flex; align-items: center; gap: 6px; font-size: 11.5px; color: #7B8A85; }
.bl-crumb-link { color: #7B8A85; text-decoration: none; } .bl-crumb-link:hover { color: #E6EFEA; }
.bl-crumb-current { color: #E6EFEA; font-weight: 600; }
.bl-search { margin-left: auto; display: flex; align-items: center; gap: 8px;
  padding: 7px 10px; border-radius: 10px; background: rgba(255,255,255,0.03);
  border: 1px solid rgba(255,255,255,0.06); min-width: 280px; color: #7B8A85; }
.bl-search input { background: transparent; border: 0; outline: 0; color: #E6EFEA; font-size: 12.5px;
  width: 100%; }
.bl-hero { position: relative; padding: 28px 30px; border-radius: 18px;
  background: linear-gradient(135deg,#0A2620 0%,#0E3C2E 55%,#124F3D 100%);
  border: 1px solid rgba(125,228,177,0.14); overflow: hidden; display: grid;
  grid-template-columns: 1.4fr 1fr; gap: 24px; }
.bl-eyebrow { display: inline-flex; align-items: center; gap: 6px; font-size: 10px;
  letter-spacing: .2em; text-transform: uppercase; color: #7DE4B1; }
.bl-eyebrow-dot { width: 6px; height: 6px; border-radius: 100%; background: #41E19A;
  box-shadow: 0 0 12px #41E19A; }
.bl-hero h1 { margin: 10px 0 8px; font-size: 34px; font-weight: 800; letter-spacing: -.5px; line-height: 1.05; }
.bl-hero h1 span { background: linear-gradient(120deg,#7DE4B1 0%,#41E19A 60%,#78C5FF 100%);
  -webkit-background-clip: text; background-clip: text; color: transparent; }
.bl-hero-copy { font-size: 13px; line-height: 1.6; color: #B4C4BE; max-width: 520px; margin: 0 0 18px; }
.bl-hero-actions { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; }
.bl-btn-primary { display: inline-flex; align-items: center; gap: 6px; padding: 10px 14px; border-radius: 10px;
  border: 0; background: linear-gradient(135deg,#1FB870 0%,#41E19A 100%); color: #04150E;
  font-weight: 700; font-size: 12.5px; cursor: pointer; }
.bl-btn-primary:disabled { opacity: .6; cursor: not-allowed; }
.bl-btn-ghost { display: inline-flex; align-items: center; gap: 6px; padding: 9px 13px; border-radius: 10px;
  background: rgba(255,255,255,0.04); border: 1px solid rgba(255,255,255,0.08);
  color: #E6EFEA; cursor: pointer; font-weight: 600; font-size: 12.5px; text-decoration: none; }
.bl-btn-ghost:hover { background: rgba(255,255,255,0.07); }
.bl-hero-preview { position: relative; display: grid; align-items: center; justify-items: center; }
.bl-mini-cover { position: absolute; width: 170px; height: 235px; border-radius: 8px;
  padding: 16px; display: flex; flex-direction: column; box-shadow: 0 24px 60px rgba(0,0,0,0.45);
  transition: transform .3s; }
.bl-mini-cover:hover { transform: translateY(-4px) rotate(0deg) !important; }
.bl-mini-tag { font-size: 9px; letter-spacing: .22em; text-transform: uppercase; opacity: .85; }
.bl-mini-title { font-size: 15px; font-weight: 800; color: #F3FFF7; margin-top: auto; line-height: 1.15; }
.bl-mini-line { width: 42px; height: 2px; margin-top: 10px; opacity: .8; }
.bl-mini-author { font-size: 9.5px; letter-spacing: .2em; text-transform: uppercase; margin-top: 8px; color: rgba(255,255,255,0.75); }
.bl-stats { margin-top: 22px; display: grid; grid-template-columns: repeat(4, 1fr); gap: 12px; }
.bl-stat { padding: 16px; border-radius: 12px; background: rgba(255,255,255,0.03);
  border: 1px solid rgba(255,255,255,0.06); }
.bl-stat-label { font-size: 10.5px; text-transform: uppercase; letter-spacing: .18em; color: #7B8A85; }
.bl-stat-row { margin-top: 12px; display: flex; align-items: baseline; justify-content: space-between; }
.bl-stat-value { font-size: 26px; font-weight: 800; letter-spacing: -.5px; }
.bl-stat-icon { width: 30px; height: 30px; border-radius: 8px; display: grid; place-items: center;
  background: rgba(65,225,154,0.1); color: #7DE4B1; }
.bl-section { margin-top: 32px; }
.bl-section-head { display: flex; align-items: center; margin-bottom: 14px; }
.bl-section-head h2 { margin: 0; font-size: 18px; font-weight: 800; letter-spacing: -.3px; }
.bl-controls { margin-left: auto; display: flex; align-items: center; gap: 8px; }
.bl-select { background: rgba(255,255,255,0.04); border: 1px solid rgba(255,255,255,0.08);
  color: #E6EFEA; padding: 6px 8px; border-radius: 8px; font-size: 11.5px; outline: 0; }
.bl-viewtoggle { display: inline-flex; border: 1px solid rgba(255,255,255,0.08); border-radius: 8px; overflow: hidden; }
.bl-viewtoggle button { padding: 6px 10px; background: transparent; border: 0; color: #7B8A85; cursor: pointer; }
.bl-viewtoggle button.active { background: rgba(65,225,154,0.15); color: #7DE4B1; }
.bl-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(260px, 1fr)); gap: 14px; }
.bl-list { display: flex; flex-direction: column; gap: 10px; }
.bl-list .bl-card { display: grid; grid-template-columns: 200px 1fr; }
.bl-list .bl-card-cover { height: 130px; }
.bl-card { position: relative; background: rgba(255,255,255,0.03); border: 1px solid rgba(255,255,255,0.06);
  border-radius: 14px; overflow: hidden; cursor: pointer; transition: border-color .2s, transform .15s; }
.bl-card:hover { border-color: rgba(125,228,177,0.35); transform: translateY(-2px); }
.bl-skel { height: 260px; animation: skel 1.2s infinite ease-in-out; }
@keyframes skel { 0%,100% { opacity:.4 } 50% { opacity:.7 } }
.bl-card-cover { position: relative; height: 210px; padding: 18px; overflow: hidden; }
.bl-card-status { position: absolute; top: 12px; left: 12px; font-size: 9.5px; padding: 3px 8px;
  border-radius: 999px; letter-spacing: .1em; text-transform: uppercase; font-weight: 700;
  background: rgba(255,255,255,0.14); color: rgba(255,255,255,0.9); }
.bl-card-status.status-in_progress { background: rgba(120,197,255,0.18); color: #B3DBFF; }
.bl-card-status.status-completed { background: rgba(125,228,177,0.18); color: #A5F0CE; }
.bl-card-status.status-draft { background: rgba(255,255,255,0.10); color: rgba(255,255,255,0.75); }
.bl-card-menu { position: absolute; top: 8px; right: 8px; height: 28px; width: 28px; border-radius: 8px;
  border: 0; background: rgba(0,0,0,0.25); color: #E6EFEA; cursor: pointer; display: grid; place-items: center; }
.bl-card-menu:hover { background: rgba(0,0,0,0.45); }
.bl-card-menu-drop { position: absolute; top: 40px; right: 10px; z-index: 30;
  background: #0C1F1B; border: 1px solid rgba(255,255,255,0.08); border-radius: 10px;
  padding: 6px; min-width: 180px; display: flex; flex-direction: column;
  box-shadow: 0 18px 48px rgba(0,0,0,0.55); }
.bl-card-menu-drop button { display: inline-flex; align-items: center; gap: 8px; padding: 8px 10px;
  border: 0; background: transparent; color: #E6EFEA; text-align: left; cursor: pointer;
  font-size: 12px; border-radius: 6px; }
.bl-card-menu-drop button:hover { background: rgba(255,255,255,0.05); }
.bl-card-menu-drop button.danger { color: #FCA5A5; }
.bl-menu-sep { height: 1px; margin: 4px 6px; background: rgba(255,255,255,0.06); }
.bl-card-inner { position: absolute; inset: 0; display: flex; flex-direction: column; padding: 42px 18px 18px; }
.bl-card-tag { font-size: 9.5px; letter-spacing: .2em; text-transform: uppercase; }
.bl-card-title { margin-top: auto; font-size: 17px; font-weight: 800; color: #FFFFFF; line-height: 1.15; }
.bl-card-decor { width: 42px; height: 2px; margin: 10px 0; }
.bl-card-author { font-size: 9.5px; letter-spacing: .2em; text-transform: uppercase; color: rgba(255,255,255,0.72); }
.bl-card-info { padding: 14px; }
.bl-card-info-row { display: flex; justify-content: space-between; align-items: baseline; gap: 10px; }
.bl-card-info-title { font-size: 13.5px; font-weight: 800; }
.bl-card-info-fmt { font-size: 10px; color: #7B8A85; letter-spacing: .12em; text-transform: uppercase; }
.bl-card-meta { display: flex; align-items: center; gap: 8px; margin-top: 4px; font-size: 11px; color: #7B8A85; }
.bl-dot { width: 3px; height: 3px; border-radius: 100%; background: #7B8A85; opacity: .6; }
.bl-progress { margin: 12px 0 8px; }
.bl-progress-head { display: flex; justify-content: space-between; font-size: 10px;
  text-transform: uppercase; letter-spacing: .12em; color: #7B8A85; }
.bl-progress-track { height: 4px; margin-top: 4px; border-radius: 4px; background: rgba(255,255,255,0.06); overflow: hidden; }
.bl-progress-fill { height: 100%; background: linear-gradient(90deg,#41E19A,#7DE4B1); }
.bl-card-actions { display: flex; align-items: center; gap: 6px; margin-top: 10px; }
.bl-btn-small { flex: 1; display: inline-flex; align-items: center; justify-content: center; gap: 6px;
  padding: 8px 10px; border-radius: 8px; background: rgba(255,255,255,0.04);
  border: 1px solid rgba(255,255,255,0.08); color: #E6EFEA; font-size: 11.5px; cursor: pointer; }
.bl-btn-small:hover { background: rgba(65,225,154,0.14); border-color: rgba(125,228,177,0.3); color: #7DE4B1; }
.bl-icon-btn { height: 32px; width: 32px; display: grid; place-items: center; border-radius: 8px;
  background: rgba(255,255,255,0.04); border: 1px solid rgba(255,255,255,0.08);
  color: #7B8A85; cursor: pointer; }
.bl-icon-btn:hover { background: rgba(120,197,255,0.14); color: #78C5FF; border-color: rgba(120,197,255,0.3); }
.bl-empty { padding: 40px; border: 1px dashed rgba(255,255,255,0.10); border-radius: 14px;
  display: flex; flex-direction: column; align-items: center; gap: 8px; text-align: center; }
.bl-empty-title { font-size: 15px; font-weight: 800; }
.bl-empty-sub { font-size: 12.5px; color: #7B8A85; max-width: 420px; }
.bl-qs { display: grid; grid-template-columns: repeat(4, 1fr); gap: 12px; }
.bl-quick { padding: 20px; border-radius: 14px; border: 1px solid rgba(255,255,255,0.06);
  background: rgba(255,255,255,0.03); text-align: left; cursor: pointer; color: #E6EFEA; }
.bl-quick:hover:not(.disabled) { border-color: rgba(125,228,177,0.35); background: rgba(255,255,255,0.05); }
.bl-quick.disabled { opacity: .5; cursor: not-allowed; }
.bl-quick-icon { width: 34px; height: 34px; border-radius: 10px; display: grid; place-items: center;
  color: #7DE4B1; background: rgba(65,225,154,0.14); }
.bl-quick-icon.tone-blue { color: #78C5FF; background: rgba(120,197,255,0.14); }
.bl-quick-icon.tone-orange { color: #FFC28A; background: rgba(255,180,120,0.16); }
.bl-quick-icon.tone-purple { color: #C6A5FF; background: rgba(198,165,255,0.15); }
.bl-quick-title { margin-top: 12px; font-size: 14px; font-weight: 800; }
.bl-quick-desc { margin-top: 4px; font-size: 12px; color: #7B8A85; line-height: 1.5; }
/* modal */
.bl-modal-backdrop { position: fixed; inset: 0; z-index: 90000; background: rgba(0,0,0,0.58);
  backdrop-filter: blur(8px); display: grid; place-items: center; padding: 24px; }
.bl-modal { width: 680px; max-width: 96vw; max-height: 92vh; background: #0B1B1B;
  border: 1px solid rgba(255,255,255,0.08); border-radius: 16px; overflow: hidden;
  display: flex; flex-direction: column; }
.bl-modal-head { display: flex; align-items: center; padding: 16px 20px; border-bottom: 1px solid rgba(255,255,255,0.06); }
.bl-modal-head h2 { margin: 3px 0 0; font-size: 18px; font-weight: 800; }
.bl-modal-eyebrow { font-size: 10px; letter-spacing: .22em; text-transform: uppercase; color: #7DE4B1; }
.bl-modal-close { margin-left: auto; height: 30px; width: 30px; border-radius: 8px;
  background: rgba(255,255,255,0.04); border: 1px solid rgba(255,255,255,0.06); color: #E6EFEA; cursor: pointer; display: grid; place-items: center; }
.bl-modal-body { padding: 18px 20px; overflow-y: auto; }
.bl-modal-hint { display: inline-flex; align-items: center; gap: 6px; padding: 8px 10px;
  background: rgba(65,225,154,0.08); border: 1px solid rgba(65,225,154,0.15); border-radius: 8px;
  font-size: 11.5px; color: #A5F0CE; margin-bottom: 14px; }
.bl-modal-hint code { background: rgba(255,255,255,0.06); padding: 1px 5px; border-radius: 4px; font-size: 10.5px; }
.bl-grid-2 { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; }
.bl-field { display: flex; flex-direction: column; gap: 4px; }
.bl-field.span { grid-column: 1 / -1; }
.bl-field-label { font-size: 10.5px; letter-spacing: .12em; text-transform: uppercase; color: #7B8A85; }
.bl-field input, .bl-field select, .bl-field textarea {
  padding: 9px 11px; border-radius: 8px; background: rgba(255,255,255,0.03);
  border: 1px solid rgba(255,255,255,0.08); color: #E6EFEA; font-size: 13px; outline: 0;
  font-family: inherit; }
.bl-field input:focus, .bl-field select:focus, .bl-field textarea:focus { border-color: rgba(65,225,154,0.5); }
.bl-modal-err { margin-top: 12px; padding: 8px 10px; border-radius: 8px;
  background: rgba(220,38,38,0.12); color: #FCA5A5; font-size: 12px; }
.bl-modal-foot { padding: 12px 20px; border-top: 1px solid rgba(255,255,255,0.06);
  display: flex; justify-content: flex-end; gap: 8px; }
/* Responsive */
@media (max-width: 1100px) {
  .bl-stats { grid-template-columns: repeat(2, 1fr); }
  .bl-qs { grid-template-columns: repeat(2, 1fr); }
  .bl-hero { grid-template-columns: 1fr; }
  .bl-hero-preview { height: 200px; }
}
@media (max-width: 780px) {
  .pl-bl-home { grid-template-columns: 1fr; }
  .bl-sidebar { position: static; height: auto; flex-direction: row; overflow-x: auto; padding: 10px 12px; gap: 8px; }
  .bl-nav { flex-direction: row; overflow-x: auto; }
  .bl-nav-item { white-space: nowrap; }
  .bl-search { min-width: 0; flex: 1; }
  .bl-grid { grid-template-columns: 1fr; }
  .bl-stats { grid-template-columns: 1fr 1fr; }
  .bl-qs { grid-template-columns: 1fr; }
  .bl-hero h1 { font-size: 26px; }
  .bl-main { padding: 14px 16px 60px; }
}
`;
